/**
 * ONE TAP — the post chain. (Art direction: SUNLIT CEL, DESIGN.md section 4.)
 *
 * Five drawing passes, and only five, because the frame budget on integrated graphics is real
 * (the sixth, DepthTapPass, issues no draw calls at all):
 *
 *   1. RenderPass          — scene -> HDR half-float buffer (RGBA; alpha carries the
 *                            selective-bloom mask, see toon.ts `setBloomMask`) plus a depth
 *                            texture for the AO pass and the light shafts.
 *   1b. DepthTapPass       — publishes that depth texture to the composite. No GPU work.
 *   2. AmbientOcclusionPass — half-res, 10-tap, depth-only SSAO with a box blur, over
 *                            min-difference reconstructed normals. Cool-tinted, tight radius;
 *                            its job is the contact line at the base of every parapet, vent and
 *                            prop, not a dirt filter. It is a supporting act outdoors — the sun
 *                            reaches the ground plane now and lays a real shadow — so it is
 *                            weaker than it was under a roof.
 *   3. SelectiveBloomPass  — masked prefilter plus a down/up pyramid. ZERO extra geometry
 *                            passes: the mask lives in the alpha channel of the main render,
 *                            so bloom is inherently occluded by world geometry and can never
 *                            wash the frame.
 *   4. SunlitCompositePass — AO, bloom, sun shafts, a lens flare gated on the sun being genuinely
 *                            unoccluded, chromatic aberration, radial speed blur, wind streaks,
 *                            kill splash, a luminance-preserving chroma boost, the high-key
 *                            tonemap, the warm-highlight/cool-shadow grade with a lifted black
 *                            point, and the sRGB encode. One fullscreen shader, so we pay the
 *                            bandwidth once.
 *   5. FXAA                — mandatory. Hard toon terminators and hairline outlines alias
 *                            viciously without it, and it runs after the sRGB encode, in the
 *                            perceptual space it was designed for.
 *
 * Deliberately absent: film grain, paper texture and radial ink speed lines. All three were
 * in the earlier ink-and-neon direction and all three are explicitly cut by DESIGN.md 4.
 *
 * This file also EXPORTS its grade (exposure, tonemap knee, black-point lift). toon.ts inverts
 * those three numbers to place its value ladder, so floors, walls and ceilings can be authored
 * as target display greys rather than as radiances someone guessed at. Change one of them and
 * the ladder re-derives; there is no second copy to forget.
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { Pass, FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';
import {
  BLOOM_RADIUS,
  BLOOM_STRENGTH,
  BLOOM_THRESHOLD,
  CHROMATIC_MAX,
  PALETTE,
} from '@shared/constants';
import { makeRng } from '@shared/math';

export type RenderQuality = 'low' | 'medium' | 'high';

/** Bloom pyramid depth per quality tier. */
const BLOOM_LEVELS: Record<RenderQuality, number> = { low: 3, medium: 4, high: 5 };
/** Soft-knee width around BLOOM_THRESHOLD, so the glow ramps in instead of popping. */
const BLOOM_KNEE = 0.35;

/**
 * THE GRADE. These four numbers are the transfer function from scene radiance to a display
 * value, and toon.ts INVERTS them to place its value ladder — floors, walls and ceilings are
 * authored as target display greys and converted back through here into the radiance that
 * produces them. So they are exported, and changing one re-derives the ladder rather than
 * silently breaking it.
 *
 * Exposure is chosen so white concrete in full sun lands at 0.94 display (CIE L* 95) — bright,
 * still short of paper white, and with the clouds and the sun disc above it. The old value sat
 * the frame on the shoulder of an ACES curve, which compressed every structural value into the
 * top eighth of the range; that is what made the arena read as fog.
 */
export const GRADE_EXPOSURE = 0.85;
/**
 * Tonemap knee. Below it the curve is EXACTLY linear, which is the point: the whole value
 * structure (walls / floors / ceilings / cast shadows) lives under this number and must not
 * be compressed. Above it an exponential shoulder approaches 1.0 asymptotically, so the sky
 * and hot emissives roll off instead of clipping and there is still real detail at the top.
 */
export const GRADE_TONE_KNEE = 0.72;
/**
 * Lifted black point (DESIGN.md 4: never crush to black). Mixed toward the cool shadow hue, so
 * the floor of the image is a blue-grey around 12% display rather than 0.
 *
 * Deliberately lower than it was. A lift of 0.05 against PALETTE.shadow put the darkest
 * possible pixel at 17% display — which is above where the ceiling band needs to sit, so the
 * lift itself was capping the value ladder from underneath.
 */
export const GRADE_SHADOW_LIFT = 0.03;
/**
 * Gentle — a high-key world does not want its corners pulled down hard, and a dark corner hides
 * players. Gentler still outdoors: the corners of the frame are now usually open sky, and a
 * vignette on the sky is the one place it reads as a lens rather than as light.
 *
 * Gentler AGAIN for the "pristine" pass. PRISTINE IS AN ABSENCE: no haze over the play space, no
 * grey wash, and no darkening that the light in the scene cannot account for. A vignette is by
 * definition the last of those, so it is turned down to the point where it does nothing but stop
 * the four corners of a two-thirds-sky frame from reading as a flat card.
 */
const VIGNETTE = 0.032;
/**
 * Speed at which chromatic aberration starts to appear at all. A stationary player must see
 * exactly none of it: visible RGB fringing on static architecture reads as a broken render,
 * not as a style choice (DESIGN.md 4 calls for it only as *speed* feedback).
 */
const CHROMATIC_SPEED_GATE = 0.55;
/**
 * Radial falloff exponent for the aberration offset. Purely radial and quartic in radius, so
 * it is identically zero across the whole centre of the frame — where the crosshair, and
 * therefore every shot in the game, lives. Quartic rather than cubic keeps the fringe off the
 * ceiling grid lines that cross the middle third of the frame, which is where it was reading
 * as a broken render rather than as speed.
 */
const CHROMATIC_FALLOFF = 4.0;
/**
 * Peak radial blur at full speed, in uv units of pull toward the centre. Kept small and
 * squared against speed: this should register as motion in the corner of your eye, not as a
 * smear you have to shoot through.
 */
const RADIAL_BLUR_MAX = 0.026;
/** Radial speed-blur taps. Jittered per pixel (see the shader), so this is a smoothness/cost
 *  dial rather than a banding one — 8 is comfortably past where the noise stops being visible. */
const RADIAL_TAPS = 8;
const WIND_STRENGTH = 0.16;
/** How fast the displayed speed catches up to the requested one. */
const SPEED_DAMP = 9.0;
/** Kill splash decay once its two hero frames are up. */
const SPLASH_DECAY = 13.0;

/**
 * WARM SUN AGAINST COOL SHADE — the whole colour story of the game (DESIGN.md 4), and outdoors
 * it is doing more work than it ever did indoors, because the value ladder pins how BRIGHT a
 * surface is and hue is the only channel left to say whether the light reaching it came from
 * the sun or from the sky. Pushed a little harder than the interior pass for exactly that
 * reason; still small enough that white concrete never reads as cream or as ice.
 */
const WARM_TINT = new THREE.Vector3(1.085, 1.005, 0.892);
const COOL_TINT = new THREE.Vector3(0.922, 0.974, 1.1);
/**
 * Where the warm/cool split sits, in post-tonemap luminance.
 *
 * These have to move whenever the value ladder does. The exterior bands land at roughly
 * 0.04-0.10 (soffit), 0.21-0.38 (deck) and 0.65-0.92 (wall) of tonemapped luminance, so the
 * window is set to straddle the deck band: soffits and shadowed ground go fully cool, a sunlit
 * deck sits near neutral, and sunlit white walls go fully warm. Any narrower and two of the
 * three planes would share an end of the split and the grade would stop describing the light.
 */
const GRADE_TINT_LO = 0.14;
const GRADE_TINT_HI = 0.62;
/**
 * ...AND THE SKY IS EXEMPT FROM IT. This is a small change with a large effect and it is worth
 * being explicit about why.
 *
 * "Warm highlights, cool shadows" is a statement about LIGHT: a surface lit by the sun goes
 * warm, a surface lit only by the sky goes cool. It is derived from luminance because for
 * ARCHITECTURE luminance is a good proxy for which of the two lit it. The sky is not
 * architecture. It is bright and it is emphatically not sunlit-white-concrete, so the proxy
 * inverts on it: the grade read the horizon band as a highlight and pushed it warm, multiplying
 * its blue channel by 0.906 and its red by 1.07 — and a measured capture of the rooftop build
 * came back with a horizon at SIX PERCENT CHROMA. The brief asks for a rich graduated blue that
 * holds up as the thing players look at; the grade was bleaching it on the way to the screen.
 *
 * So sky pixels take the tonemap, the black-point lift, the bloom, the shafts and the dither
 * like everything else, and skip the tint. sky.ts already authors the dome's hue against the
 * palette; nothing downstream should be repainting it. Measured effect: horizon haze 6% -> 22%
 * chroma, far skyline band 9% -> 23%, with no value change anywhere and no effect at all on any
 * surface a player can stand on.
 *
 * The test is the same one the AO pass and the shaft march use — the sky is the only thing in
 * the frame at the far plane, because the sky quad draws with `depthWrite: false` and therefore
 * leaves the cleared depth exactly 1.0 behind it. When no depth texture is bound the stand-in
 * reads 0 and every pixel takes the normal grade, which is the safe direction to fail.
 */
const SKY_DEPTH = 0.999995;
/**
 * CHROMA. A pure saturation gain applied in scene radiance, before the tonemap.
 *
 * Playtest, twice over: "it still feels a little muted, like there could be more splashes of
 * colour", and "it could feel more sunny — insanely pristine." Pristine is not brightness, it is
 * PURITY: clean saturated colour that has not been greyed on its way to the screen. Every stage
 * of this pipeline is subtractive about chroma — the tonemap's shoulder desaturates anything
 * with a hot channel, the black-point lift mixes a grey-blue into everything, and AO multiplies
 * by a tint — and there was no stage anywhere that put chroma back.
 *
 * IT IS LUMINANCE-PRESERVING BY CONSTRUCTION, which is what makes it safe to add underneath a
 * value ladder that has now failed twice. `mix( vec3( luma ), col, k )` has luminance exactly
 * `luma` for any k, because the mix is affine and luma( vec3( luma ) ) == luma. So for the
 * architecture the ladder is quoted against — PALETTE.base, a near-neutral white — this is
 * arithmetically the identity, and the six band numbers in toon.ts do not move by a thousandth.
 * What it moves is everything the ladder deliberately does NOT own: team colours, the amber gun,
 * wayfinding blocks, the sky, and the warm/cool split that toon.ts paints onto white concrete.
 *
 * Applied PRE-tonemap on purpose. Post-tonemap the values are already inside [0,1] and a boost
 * would clip saturated highlights flat; pre-tonemap the shoulder catches the overshoot and rolls
 * it off, which is what the shoulder is for. 1.12 is deliberately modest — this is a white city
 * (DESIGN.md 4: colour means something because there is so little of it), so the job is to stop
 * the pipeline from stealing chroma, not to add chroma that was never authored.
 *
 * IT WAS NEVER ACTUALLY WIRED UP. This constant, and the four paragraphs above it, existed in
 * the file while the composite shader had no saturation stage in it at all — declared, argued
 * for, measured, and then dead. Which is the likeliest single explanation for a playtest that
 * kept saying "muted" at a pipeline whose own comments claimed the chroma had already been put
 * back. Measured now that it runs: the zenith goes from 68% HSV chroma to 76%, the sky at twelve
 * degrees from 46% to 51%, a shadowed deck from 21% to 27%, and PALETTE.base white does not move
 * by a thousandth of an L* because for a neutral the operator is arithmetically the identity.
 */
const GRADE_SATURATION = 1.12;

/**
 * CONTACT SHADOWS. Most of what makes an object feel placed rather than floating, and in a
 * white world they are doing the job an albedo change would do anywhere else. Round one had
 * this pass running and it was invisible in every screenshot, for a specific reason: the
 * grazing-angle guard (see AO_FACING_*) faded it out on exactly the surface an FPS camera
 * spends its life looking at — a floor, seen nearly edge-on, receding from your feet. That is
 * the only place a contact shadow is ever *wanted*.
 *
 * So the normal reconstruction is now the min-difference kind, which does not blow up on
 * silhouette edges, and the guard it existed to compensate for is nearly off.
 */
const AO_SAMPLES = 10;
/**
 * Sample radius in metres. Tight: this is a contact term, not an occlusion pass. Anything
 * wider starts shading whole wall panels, which reads as dirt on the architecture and fights
 * the value ladder rather than supporting it.
 */
const AO_RADIUS = 0.85;
const AO_BIAS = 0.02;
/**
 * Pulled back AGAIN with the move outdoors, and AO_FLOOR raised to match.
 *
 * Indoors this was the only contact cue there was — a slab over every deck meant the sun was
 * shadowed away from everything a player walked on, so a cast shadow contributed nothing at the
 * base of a pillar and AO had to stand in for it. On an open roof the sun reaches the ground
 * plane and every object standing on it lays a real, long, shaped shadow. AO's job shrinks back
 * to what it should always have been: the tight dark line where two surfaces actually meet.
 *
 * That matters for the ladder, because AO multiplies the graded colour and is therefore the one
 * pass in the chain that can take a surface off its rung. At 1.35/0.60 a fully occluded corner
 * was a 0.66 multiply — about seven L* points. At 1.15/0.68 it is 0.72, about five. Five points
 * of contact darkening inside a sixteen-point gap is a gradient; it is not a band change.
 */
const AO_INTENSITY = 1.15;
/** How dark AO is allowed to go, and in what hue. Cool-tinted, never grey (DESIGN.md 4). */
const AO_FLOOR = 0.68;
/**
 * Grazing-angle guard, in view-space normal Z. Only pixels whose reconstructed normal is
 * within a few degrees of perpendicular to the view are dropped — those are silhouette edges
 * where depth-derived normals are genuinely meaningless. Everything else keeps its AO.
 */
const AO_FACING_LO = 0.04;
const AO_FACING_HI = 0.16;

/**
 * Bloom mask gate. Alpha carries `1 - bloomMask`, but alpha-blended geometry that never
 * opted in still leaves a partial value behind: blending opacity `o` over an opaque backdrop
 * lands the buffer at `o*o + (1-o)`, which bottoms out at 0.75 — a phantom mask of up to
 * 0.25. Glass curtain walls, decals and soft VFX were all quietly blooming because of it.
 * Gating above that phantom ceiling leaves only deliberate emitters, which write 0.0 exactly.
 */
const BLOOM_MASK_GATE_LO = 0.3;
const BLOOM_MASK_GATE_HI = 0.6;
/**
 * How much of the brightness threshold a fully-masked pixel is let off. The mask already
 * proves the pixel is an emitter, so a further brightness test only serves to shape the
 * glow — and the amber "who is armed?" cue must never be reduced to a smudge.
 */
const BLOOM_EMITTER_DISCOUNT = 0.65;

/**
 * HIGHLIGHT BLOOM — the SPARKLE half of "it should feel like a beautiful day".
 *
 * The mask path above is a binary question: did this material declare itself an emitter? That is
 * exactly right for the amber gun and exactly useless for the thing a sunny day is actually made
 * of, which is a glint. A crisp specular core on a glass curtain wall, a steel handrail or still
 * water is not an emitter and must not be tagged as one — but it IS a point of light, and a point
 * of light that does not bleed is a lighter patch of surface instead.
 *
 * So there is a second, unmasked path with a much higher threshold, and the whole design is in
 * where that threshold sits. Measured in scene radiance, through toon.ts's bands, on the retuned
 * rig (the max CHANNEL, since the key is warm and red is what clears a threshold first):
 *
 *     white concrete in full sun, the brightest DIFFUSE thing in the game     1.265
 *     HIGHLIGHT_THRESHOLD                                                     1.30
 *     spec block on the arena's dullest opted-in material (uSpec 0.14)        1.60
 *     spec glint core, uSpec 0.14 -> 0.40                                1.80 -> 2.92
 *
 * WHAT THE KNEE ACTUALLY DOES HERE IS WORTH STATING, because the threshold alone reads as a
 * harder gate than it is. `softThreshold` starts contributing at THRESHOLD - KNEE, i.e. at 0.85
 * of radiance, so sunlit white concrete has always passed a little of itself into the pyramid —
 * about 6.8% at the old numbers. That is not a bug, it is the brief: DESIGN.md 4 wants sunlit
 * white sitting just under clipping with its brightest EDGES blooming, and a hard gate above the
 * diffuse maximum would mean only opted-in specular could ever glow.
 *
 * So the retune moves it deliberately and by a small amount. At 1.30 / 0.50 the top of the wall
 * band passes 7.6% and lands, after BLOOM_STRENGTH, as +0.026 of radiance on a flat sunlit wall —
 * roughly four tenths of an L*, invisible as a wash but enough that a parapet edge against the
 * sky gets a whisper of halo. A shaded wall (0.79) and a sunlit deck (0.46) are both under the
 * knee's toe and contribute exactly zero, which is what keeps this from becoming the global haze
 * DESIGN.md 4 rules out by name. The glint, meanwhile, gained a whole extra threshold of
 * clearance, so the ratio of sparkle-to-wash went up by considerably more than the wash did.
 *
 * If either file is retuned, these four numbers are the check — and the one that must not be
 * violated is that the flat DIFFUSE maximum stays under THRESHOLD - KNEE + a margin, i.e. that
 * a wall contributes single-digit percentages and a glint contributes tens.
 *
 * SKY PIXELS ARE EXCLUDED, by the same far-plane depth test the shafts and the grade use. The
 * dome is authored at up to 1.5 of radiance and would light the entire top of the frame — which
 * is the global haze DESIGN.md 4 rules out in as many words. The sky does its own blooming
 * through the mask (the disc, the halo, the clouds' silver lining) where it is meant to.
 */
const HIGHLIGHT_THRESHOLD = 1.3;
const HIGHLIGHT_KNEE = 0.45;
/** How much of a cleared highlight reaches the pyramid. Subtle: a gleam, not a glow. */
const HIGHLIGHT_GAIN = 0.5;

/**
 * LIGHT SHAFTS / GOD RAYS. Nothing sells "sunny" like a shaft, and outdoors the sun is in frame
 * a great deal of the time, so this went from a nice-to-have in an interior to one of the two
 * things (with the cast shadow) that carry "this is daylight".
 *
 * This is the classic screen-space radial occlusion march, with the depth buffer standing in
 * for the occlusion mask — every tap that lands on the far plane is a tap that can see sky.
 * Twelve taps of a single depth texture, in a pass that is already running, so it is close to
 * free; the alternative (a second scene render into a low-res occlusion buffer) is not. Because
 * it is that cheap, IT NOW RUNS AT EVERY QUALITY TIER — the same lesson the shadow map learned
 * the hard way (renderer.ts): a reviewer on a slow machine judging a code path where the
 * signature effect cannot exist is a review round thrown away.
 *
 * DENSITY is the fraction of the distance to the sun's screen position that the march covers,
 * so the streak length is automatically proportional to how far off-axis the sun is. FALLOFF
 * is exponential in *aspect-corrected* screen distance from the sun, so the effect stays a
 * shaft near the sun rather than becoming a global haze — DESIGN.md 4 rules out a global haze
 * explicitly, and a bright wash is exactly where an enemy silhouette gets lost. It is looser
 * than the interior tuning (1.9 -> 1.55) because the shafts now come off a parapet edge or a
 * mast a long way from the disc, not out of a window ten metres away.
 */
const SHAFT_TAPS = 12;
const SHAFT_DENSITY = 0.7;
const SHAFT_DECAY = 0.955;
const SHAFT_FALLOFF = 1.55;
const SHAFT_STRENGTH = 0.66;

/**
 * LENS FLARE — and the whole design is in the word ONLY.
 *
 * DESIGN.md 4 and the brief agree: a gentle flare is the single cheapest "beautiful day" cue
 * there is, and it is also the single fastest way to make a game look cheap. The difference
 * between the two is entirely whether it is honest — whether the flare appears if and only if
 * the player can actually see the sun. A flare that survives the sun going behind a plant room
 * is not a lens artefact, it is a decal, and every player reads it as one instantly.
 *
 * So this is gated on a REAL OCCLUSION TEST, not on a dot product. Five taps of the depth buffer
 * in a small cross at the sun's own screen position: a tap that reads the far plane is a tap that
 * can see sky, which is the same test the shafts, the grade and the highlight bloom already use,
 * and the depth texture is already bound. Five taps rather than one so the flare fades over a few
 * frames as a mast or a parapet crosses the disc instead of popping. Cost is five texture reads
 * inside a UNIFORM branch that is skipped entirely whenever the sun is behind the camera or
 * off-frame, i.e. most of the time, and nothing at all on the geometry side.
 *
 * WHAT IT DRAWS, in ascending order of how carefully it has to be restrained:
 *
 *  - A warm bloom around the disc. Two exponential lobes, the wide one very faint. This is the
 *    part that reads as "bright day" rather than as "camera", and it is most of the effect.
 *  - A soft anamorphic streak through the disc. Horizontal, tight in y, wide in x.
 *  - Three ghosts on the sun -> screen-centre axis. THESE ARE THE READABILITY RISK, because they
 *    are the only part of the effect that lands in the middle of the frame where the crosshair
 *    and the enemies are, so they are the faintest thing here by a factor of five: peak 0.031 of
 *    scene radiance against a shaded deck at 0.25. Visible as a warm bloom passing over the
 *    world; nowhere near able to hide a player-sized silhouette.
 *
 * All of it is added PRE-TONEMAP, so the shoulder rolls it off exactly the way it rolls off the
 * sun disc itself, and all of it is the sun's own hue — no rainbow ghosts, no blue streaks. This
 * is a bright morning seen through a clean lens, not a JJ Abrams film.
 */
const FLARE_STRENGTH = 0.22;
/** Sudden death burns harder, in step with the glare and the shafts. See NOON_GLARE. */
const FLARE_NOON = 0.6;
/** Ghost positions, as multiples of the sun -> centre vector, and their gains and tightnesses. */
const FLARE_GHOSTS = [
  { at: 1.35, gain: 0.14, tight: 260.0 },
  { at: 1.78, gain: 0.1, tight: 90.0 },
  { at: 2.25, gain: 0.08, tight: 420.0 },
] as const;

/**
 * HIGH NOON (DESIGN.md 2), and specifically what it is NOT.
 *
 * The previous implementation of sudden death graded the whole frame amber. That is a recolour,
 * and it destroys the one thing this art direction is built on: a white city under a blue sky,
 * where colour means something *because there is so little of it* (DESIGN.md 4). A full-frame
 * wash spends the entire colour budget on a state change and takes the team colours, the amber
 * gun glow and the wayfinding blocks down with it — the amber gun becomes invisible in an amber
 * world at precisely the moment the round is being decided.
 *
 * So HIGH NOON escalates the LIGHT instead, in four ways, none of which touches a hue:
 *
 *  1. CONTRAST, as a pure gamma on the tonemapped value. Gamma is the right operator here
 *     because it fixes both endpoints exactly: white stays white, black stays wherever the
 *     lifted black point put it, and everything between darkens on a smooth curve. A linear
 *     contrast stretch around a pivot would have crushed soffits to zero (measured: they go
 *     negative at any useful strength), which DESIGN.md 4 forbids outright. Measured through
 *     the grade, 1.16 widens the frame's whole spread and drops the deck bands by 4-6 L*
 *     while every gap in the value ladder stays above 15.
 *  2. SHADOWS LENGTHEN AND DEEPEN, by dropping the sun and raising the shadow intensity. That
 *     lives in lighting.ts; it is the loudest half of the effect and it costs nothing.
 *  3. SUN GLARE. The bloom pyramid gets stronger and the shafts get longer, so the sun starts
 *     to burn. Warm light around a warm sun is not a recolour of the world.
 *  4. A soft warm glare disc around the sun's screen position, gated on the sun actually being
 *     in frame, so the effect has somewhere to originate even against a clear sky.
 */
const NOON_GAMMA = 0.16;
const NOON_BLOOM = 0.85;
const NOON_SHAFT = 0.7;
const NOON_GLARE = 0.24;
const NOON_GLARE_FALLOFF = 2.6;

// ---------------------------------------------------------------------------
// Shared GLSL
// ---------------------------------------------------------------------------

const HASH_GLSL = /* glsl */ `
float hash11( float p ) {

	p = fract( p * 0.1031 );
	p *= p + 33.33;
	p *= p + p;
	return fract( p );

}

float hash12( vec2 p ) {

	vec3 q = fract( vec3( p.xyx ) * 0.1031 );
	q += dot( q, q.yzx + 33.33 );
	return fract( ( q.x + q.y ) * q.z );

}
`;

const FULLSCREEN_VERT = /* glsl */ `
varying vec2 vUv;

void main() {

	vUv = uv;
	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

}
`;

const _clearColor = new THREE.Color();

/** Renormalise a colour to luminance 1 so it tints hue without changing exposure. */
function hueOnly(hex: number): THREE.Color {
  const c = new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
  const luma = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
  if (luma > 1e-4) c.multiplyScalar(1 / luma);
  return c;
}

function makeColorTarget(w: number, h: number, name: string): THREE.WebGLRenderTarget {
  const rt = new THREE.WebGLRenderTarget(w, h, {
    type: THREE.HalfFloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    depthBuffer: false,
    stencilBuffer: false,
    generateMipmaps: false,
  });
  rt.texture.name = name;
  return rt;
}

// ---------------------------------------------------------------------------
// Ambient occlusion
// ---------------------------------------------------------------------------

const AO_FRAG = /* glsl */ `
uniform sampler2D tDepth;
uniform mat4 uProjection;
uniform mat4 uInvProjection;
uniform vec2 uResolution;
uniform vec3 uKernel[ ${AO_SAMPLES} ];
uniform float uRadius;
uniform float uBias;
uniform float uIntensity;

varying vec2 vUv;

${HASH_GLSL}

vec3 viewPosition( vec2 uv, float depth ) {

	vec4 clip = vec4( uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0 );
	vec4 view = uInvProjection * clip;
	return view.xyz / view.w;

}

void main() {

	float depth = texture2D( tDepth, vUv ).x;
	vec3 p = viewPosition( vUv, depth );

	// MIN-DIFFERENCE NORMALS. A plain cross( dFdx( p ), dFdy( p ) ) is one texel of depth noise
	// away from a wildly wrong normal, and at a silhouette it straddles two surfaces and
	// produces garbage — which is why the old code had to fade AO out at grazing angles, and
	// why it therefore had no AO on the receding floor an FPS camera stares down all match.
	//
	// Taking both the forward and the backward difference on each axis and keeping whichever
	// is the smaller depth step means an edge pixel picks the neighbour on ITS OWN surface. The
	// normal stays correct right up to the silhouette, so the guard can be nearly off.
	vec2 texel = 1.0 / uResolution;
	vec3 pl = viewPosition( vUv - vec2( texel.x, 0.0 ), texture2D( tDepth, vUv - vec2( texel.x, 0.0 ) ).x );
	vec3 pr = viewPosition( vUv + vec2( texel.x, 0.0 ), texture2D( tDepth, vUv + vec2( texel.x, 0.0 ) ).x );
	vec3 pd = viewPosition( vUv - vec2( 0.0, texel.y ), texture2D( tDepth, vUv - vec2( 0.0, texel.y ) ).x );
	vec3 pu = viewPosition( vUv + vec2( 0.0, texel.y ), texture2D( tDepth, vUv + vec2( 0.0, texel.y ) ).x );

	vec3 ddx = abs( pr.z - p.z ) < abs( p.z - pl.z ) ? pr - p : p - pl;
	vec3 ddy = abs( pu.z - p.z ) < abs( p.z - pd.z ) ? pu - p : p - pd;

	vec3 n = cross( ddx, ddy );
	n = length( n ) > 1e-9 ? normalize( n ) : vec3( 0.0, 0.0, 1.0 );
	if ( n.z < 0.0 ) n = - n;

	// Per-pixel rotation of the kernel — turns banding into fine noise that the blur eats.
	float ang = hash12( vUv * uResolution ) * 6.2831853;
	vec3 rv = vec3( cos( ang ), sin( ang ), 0.0 );
	vec3 t = normalize( rv - n * dot( rv, n ) );
	vec3 b = cross( n, t );
	mat3 tbn = mat3( t, b, n );

	float pz = - p.z;
	// Depth-proportional bias: one screen pixel covers more world at range, so a fixed bias
	// that is right up close self-occludes into stripes far away.
	float bias = uBias * ( 1.0 + pz * 0.03 );
	float occlusion = 0.0;

	for ( int i = 0; i < ${AO_SAMPLES}; i ++ ) {

		vec3 sp = p + tbn * uKernel[ i ] * uRadius;
		vec4 clip = uProjection * vec4( sp, 1.0 );
		vec2 suv = ( clip.xy / clip.w ) * 0.5 + 0.5;

		float sceneDepth = texture2D( tDepth, clamp( suv, vec2( 0.0 ), vec2( 1.0 ) ) ).x;
		float sceneZ = - viewPosition( suv, sceneDepth ).z;
		float sampleZ = - sp.z;

		// Reject occluders that are far in front — otherwise a foreground pillar shades the
		// entire background behind it.
		float rangeCheck = smoothstep( 0.0, 1.0, uRadius / max( abs( sceneZ - pz ), 1e-4 ) );
		occlusion += ( sceneZ <= sampleZ - bias ? 1.0 : 0.0 ) * rangeCheck;

	}

	float ao = 1.0 - ( occlusion / float( ${AO_SAMPLES} ) ) * uIntensity;

	// n.z is the cosine to the viewer. Only true silhouette pixels — where even a min-difference
	// normal has nothing sensible to reconstruct from — are dropped.
	float facing = smoothstep( ${AO_FACING_LO.toFixed(3)}, ${AO_FACING_HI.toFixed(3)}, n.z );
	ao = mix( 1.0, ao, facing );

	// The far plane is sky: never occlude it.
	float sky = step( 0.999995, depth );
	gl_FragColor = vec4( mix( clamp( ao, 0.0, 1.0 ), 1.0, sky ), 0.0, 0.0, 1.0 );

}
`;

const AO_BLUR_FRAG = /* glsl */ `
uniform sampler2D tAo;
uniform vec2 uTexel;
varying vec2 vUv;

void main() {

	// 4 bilinear taps = a 4x4 box. Enough to erase the per-pixel kernel rotation noise
	// without smearing a contact shadow off its corner.
	float a = texture2D( tAo, vUv + vec2( - 1.5, - 1.5 ) * uTexel ).r;
	a += texture2D( tAo, vUv + vec2( 1.5, - 1.5 ) * uTexel ).r;
	a += texture2D( tAo, vUv + vec2( - 1.5, 1.5 ) * uTexel ).r;
	a += texture2D( tAo, vUv + vec2( 1.5, 1.5 ) * uTexel ).r;
	gl_FragColor = vec4( a * 0.25, 0.0, 0.0, 1.0 );

}
`;

class AmbientOcclusionPass extends Pass {
  private aoTarget: THREE.WebGLRenderTarget;
  private blurTarget: THREE.WebGLRenderTarget;
  private readonly aoMaterial: THREE.ShaderMaterial;
  private readonly blurMaterial: THREE.ShaderMaterial;
  private readonly quad: FullScreenQuad;
  private readonly whiteTexture: THREE.DataTexture;
  private enabledFlag = true;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly onResult: (t: THREE.Texture) => void,
  ) {
    super();
    this.needsSwap = false;

    const kernel: THREE.Vector3[] = [];
    const rng = makeRng(0x51a0c7);
    for (let i = 0; i < AO_SAMPLES; i++) {
      // Cosine-ish hemisphere points, pulled toward the origin so most samples probe close
      // in. That is what gives tight contact shadows instead of a broad dirt haze.
      const v = new THREE.Vector3(rng() * 2 - 1, rng() * 2 - 1, rng() * 0.9 + 0.1).normalize();
      const t = i / AO_SAMPLES;
      v.multiplyScalar(0.25 + 0.75 * t * t);
      kernel.push(v);
    }

    this.aoMaterial = new THREE.ShaderMaterial({
      name: 'OneTapAO',
      uniforms: {
        tDepth: { value: null },
        uProjection: { value: new THREE.Matrix4() },
        uInvProjection: { value: new THREE.Matrix4() },
        uResolution: { value: new THREE.Vector2(1, 1) },
        uKernel: { value: kernel },
        uRadius: { value: AO_RADIUS },
        uBias: { value: AO_BIAS },
        uIntensity: { value: AO_INTENSITY },
      },
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: AO_FRAG,
      depthTest: false,
      depthWrite: false,
    });

    this.blurMaterial = new THREE.ShaderMaterial({
      name: 'OneTapAOBlur',
      uniforms: { tAo: { value: null }, uTexel: { value: new THREE.Vector2() } },
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: AO_BLUR_FRAG,
      depthTest: false,
      depthWrite: false,
    });

    this.quad = new FullScreenQuad(this.aoMaterial);
    this.aoTarget = makeColorTarget(4, 4, 'OneTapAO');
    this.blurTarget = makeColorTarget(4, 4, 'OneTapAOBlur');

    const white = new Uint8Array([255, 255, 255, 255]);
    this.whiteTexture = new THREE.DataTexture(white, 1, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this.whiteTexture.name = 'AOOff';
    this.whiteTexture.needsUpdate = true;
  }

  setEnabled(on: boolean): void {
    this.enabledFlag = on;
    if (!on) this.onResult(this.whiteTexture);
  }

  override setSize(width: number, height: number): void {
    const w = Math.max(4, Math.floor(width * 0.5));
    const h = Math.max(4, Math.floor(height * 0.5));
    this.aoTarget.setSize(w, h);
    this.blurTarget.setSize(w, h);
    (this.aoMaterial.uniforms.uResolution!.value as THREE.Vector2).set(w, h);
    (this.blurMaterial.uniforms.uTexel!.value as THREE.Vector2).set(1 / w, 1 / h);
  }

  override render(
    renderer: THREE.WebGLRenderer,
    _writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    const depth = readBuffer.depthTexture;
    if (!this.enabledFlag || depth === null || depth === undefined) {
      this.onResult(this.whiteTexture);
      return;
    }

    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;

    this.aoMaterial.uniforms.tDepth!.value = depth;
    (this.aoMaterial.uniforms.uProjection!.value as THREE.Matrix4).copy(this.camera.projectionMatrix);
    (this.aoMaterial.uniforms.uInvProjection!.value as THREE.Matrix4).copy(
      this.camera.projectionMatrixInverse,
    );

    this.quad.material = this.aoMaterial;
    renderer.setRenderTarget(this.aoTarget);
    this.quad.render(renderer);

    this.blurMaterial.uniforms.tAo!.value = this.aoTarget.texture;
    this.quad.material = this.blurMaterial;
    renderer.setRenderTarget(this.blurTarget);
    this.quad.render(renderer);

    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;

    this.onResult(this.blurTarget.texture);
  }

  override dispose(): void {
    this.aoTarget.dispose();
    this.blurTarget.dispose();
    this.aoMaterial.dispose();
    this.blurMaterial.dispose();
    this.whiteTexture.dispose();
    this.quad.dispose();
  }
}

/**
 * Hands the scene buffer's depth texture to whoever asks for it, once per frame, and costs
 * nothing at all.
 *
 * The light-shaft march needs depth in the COMPOSITE pass, and a ShaderPass only ever binds
 * `readBuffer.texture`. Rather than special-case the composite (or route depth through the AO
 * pass, which is switched off on the low tier and would take the shafts down with it), the tap
 * sits in the chain as its own no-op pass and publishes what it sees.
 */
class DepthTapPass extends Pass {
  constructor(private readonly onDepth: (t: THREE.Texture | null) => void) {
    super();
    this.needsSwap = false;
  }

  override render(
    _renderer: THREE.WebGLRenderer,
    _writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    this.onDepth(readBuffer.depthTexture ?? null);
  }
}

// ---------------------------------------------------------------------------
// Bloom pyramid
// ---------------------------------------------------------------------------

const PREFILTER_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform sampler2D tDepth;
uniform vec2 uTexel;
uniform float uThreshold;
uniform float uKnee;
uniform float uHighlight;
varying vec2 vUv;

/** Soft-knee threshold, returning the fraction of br that survives it. */
float softThreshold( float br, float threshold, float knee ) {

	float soft = clamp( br - threshold + knee, 0.0, 2.0 * knee );
	soft = soft * soft / ( 4.0 * knee + 1e-4 );
	return max( soft, br - threshold ) / max( br, 1e-4 );

}

void main() {

	vec2 o = uTexel;
	vec4 s0 = texture2D( tSrc, vUv + vec2( - o.x, - o.y ) );
	vec4 s1 = texture2D( tSrc, vUv + vec2(   o.x, - o.y ) );
	vec4 s2 = texture2D( tSrc, vUv + vec2( - o.x,   o.y ) );
	vec4 s3 = texture2D( tSrc, vUv + vec2(   o.x,   o.y ) );
	vec4 s = ( s0 + s1 + s2 + s3 ) * 0.25;

	// THE selective part. Alpha is written as ( 1 - bloomMask ) by every material that opts
	// in; anything that never opts in sits at 1.0 and contributes exactly nothing.
	//
	// The gate is what keeps that promise in the presence of alpha blending: a transparent
	// surface leaves a partial alpha in the buffer even though it never asked to glow, and
	// without the gate every pane of glass in the arena bloomed. See BLOOM_MASK_GATE_*.
	float rawMask = clamp( 1.0 - s.a, 0.0, 1.0 );
	float mask = smoothstep( ${BLOOM_MASK_GATE_LO.toFixed(3)}, ${BLOOM_MASK_GATE_HI.toFixed(3)}, rawMask );
	vec3 c = max( s.rgb, vec3( 0.0 ) ) * mask;

	// Soft-knee threshold, scaled DOWN by how emissive the pixel is.
	//
	// A brightness threshold exists to separate emitters from bright diffuse when you cannot
	// tell them apart. Here we can: the mask already says so. And this world is white and
	// sunlit, so a lit wall sits well above BLOOM_THRESHOLD while an amber gun barely clears
	// it — applying the full threshold to a known emitter would leave the single most
	// important readability cue in the game ("who is armed?") as a faint smudge. So a fully
	// masked pixel is let off most of the threshold, and partial (antialiased edge) pixels
	// slide back toward the full one.
	float threshold = uThreshold * ( 1.0 - ${BLOOM_EMITTER_DISCOUNT.toFixed(3)} * mask );
	float br = max( c.r, max( c.g, c.b ) );
	float contrib = softThreshold( br, threshold, uKnee );

	// HIGHLIGHT BLOOM: a second, unmasked path with a much higher threshold, so a specular glint
	// bleeds without having to lie about being an emitter. Excluded from the sky (which would
	// become a global haze) and from pixels the mask already claimed (which would double-count).
	// See HIGHLIGHT_THRESHOLD for the four radiance figures that place the threshold.
	vec3 raw = max( s.rgb, vec3( 0.0 ) );
	float sky = step( ${SKY_DEPTH.toFixed(6)}, texture2D( tDepth, vUv ).x );
	float rawBr = max( raw.r, max( raw.g, raw.b ) );
	float hi = softThreshold( rawBr, ${HIGHLIGHT_THRESHOLD.toFixed(3)}, ${HIGHLIGHT_KNEE.toFixed(3)} );
	vec3 highlight = raw * ( hi * uHighlight * ( 1.0 - sky ) * ( 1.0 - mask ) );

	gl_FragColor = vec4( c * contrib + highlight, 1.0 );

}
`;

const DOWNSAMPLE_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform vec2 uTexel;
varying vec2 vUv;

void main() {

	vec2 o = uTexel;
	vec3 c = texture2D( tSrc, vUv + vec2( - o.x, - o.y ) ).rgb;
	c += texture2D( tSrc, vUv + vec2(   o.x, - o.y ) ).rgb;
	c += texture2D( tSrc, vUv + vec2( - o.x,   o.y ) ).rgb;
	c += texture2D( tSrc, vUv + vec2(   o.x,   o.y ) ).rgb;
	gl_FragColor = vec4( c * 0.25, 1.0 );

}
`;

const UPSAMPLE_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform vec2 uTexel;
uniform float uRadius;
varying vec2 vUv;

void main() {

	vec2 o = uTexel * uRadius;
	// 3x3 tent — wider and softer than a box, which is what keeps the halo from banding.
	vec3 c = texture2D( tSrc, vUv + vec2( - o.x, - o.y ) ).rgb;
	c += texture2D( tSrc, vUv + vec2( 0.0, - o.y ) ).rgb * 2.0;
	c += texture2D( tSrc, vUv + vec2(   o.x, - o.y ) ).rgb;
	c += texture2D( tSrc, vUv + vec2( - o.x, 0.0 ) ).rgb * 2.0;
	c += texture2D( tSrc, vUv ).rgb * 4.0;
	c += texture2D( tSrc, vUv + vec2(   o.x, 0.0 ) ).rgb * 2.0;
	c += texture2D( tSrc, vUv + vec2( - o.x,   o.y ) ).rgb;
	c += texture2D( tSrc, vUv + vec2( 0.0,   o.y ) ).rgb * 2.0;
	c += texture2D( tSrc, vUv + vec2(   o.x,   o.y ) ).rgb;
	gl_FragColor = vec4( c * ( 1.0 / 16.0 ), 1.0 );

}
`;

class BloomChain {
  private targets: THREE.WebGLRenderTarget[] = [];
  private readonly prefilterMat: THREE.ShaderMaterial;
  private readonly downMat: THREE.ShaderMaterial;
  private readonly upMat: THREE.ShaderMaterial;
  private readonly quad: FullScreenQuad;
  private readonly blackTexture: THREE.DataTexture;
  /**
   * Depth stand-in for the highlight path. Reads as 1.0 — "every pixel is sky" — so if no depth
   * buffer is ever bound the unmasked highlight bloom switches itself off entirely. Failing to
   * "no extra glow" is the safe direction; failing to "the whole sky glows" is the effect this
   * art direction rules out by name.
   */
  private readonly skyTexture: THREE.DataTexture;
  private levels = BLOOM_LEVELS.high;
  private width = 2;
  private height = 2;

  constructor() {
    this.prefilterMat = new THREE.ShaderMaterial({
      name: 'BloomPrefilter',
      uniforms: {
        tSrc: { value: null },
        tDepth: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uThreshold: { value: BLOOM_THRESHOLD },
        uKnee: { value: BLOOM_KNEE },
        uHighlight: { value: HIGHLIGHT_GAIN },
      },
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: PREFILTER_FRAG,
      depthTest: false,
      depthWrite: false,
    });

    this.downMat = new THREE.ShaderMaterial({
      name: 'BloomDown',
      uniforms: { tSrc: { value: null }, uTexel: { value: new THREE.Vector2() } },
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: DOWNSAMPLE_FRAG,
      depthTest: false,
      depthWrite: false,
    });

    this.upMat = new THREE.ShaderMaterial({
      name: 'BloomUp',
      uniforms: {
        tSrc: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uRadius: { value: 1 + BLOOM_RADIUS * 2 },
      },
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: UPSAMPLE_FRAG,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      transparent: true,
    });

    this.quad = new FullScreenQuad(this.prefilterMat);

    const black = new Uint8Array(4);
    this.blackTexture = new THREE.DataTexture(black, 1, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this.blackTexture.name = 'BloomOff';
    this.blackTexture.needsUpdate = true;

    const white = new Uint8Array([255, 255, 255, 255]);
    this.skyTexture = new THREE.DataTexture(white, 1, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this.skyTexture.name = 'HighlightDepthOff';
    this.skyTexture.needsUpdate = true;
    this.prefilterMat.uniforms.tDepth!.value = this.skyTexture;
  }

  setLevels(levels: number): void {
    if (levels === this.levels) return;
    this.levels = levels;
    this.rebuild();
  }

  setSize(width: number, height: number): void {
    this.width = Math.max(4, Math.floor(width));
    this.height = Math.max(4, Math.floor(height));
    this.rebuild();
  }

  private rebuild(): void {
    for (const t of this.targets) t.dispose();
    this.targets = [];
    let w = Math.max(4, this.width >> 1);
    let h = Math.max(4, this.height >> 1);
    for (let i = 0; i < this.levels; i++) {
      this.targets.push(makeColorTarget(w, h, 'OneTapBloomMip'));
      w = Math.max(4, w >> 1);
      h = Math.max(4, h >> 1);
    }
  }

  render(
    renderer: THREE.WebGLRenderer,
    source: THREE.Texture,
    depth: THREE.Texture | null,
    srcW: number,
    srcH: number,
  ): THREE.Texture {
    if (this.levels <= 0 || this.targets.length === 0) return this.blackTexture;

    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.getClearColor(_clearColor);
    const prevAlpha = renderer.getClearAlpha();
    renderer.autoClear = false;
    renderer.setClearColor(0x000000, 0);

    // Prefilter: full-res source -> half-res mip 0, applying the mask + threshold.
    //
    // BINDING THE DEPTH TEXTURE IS LOAD-BEARING, not defensive. The unmasked highlight path
    // multiplies by ( 1 - sky ), and with nothing bound that sampler reads black on every driver
    // that defines the case at all — i.e. "no pixel is sky", which lets the dome's own radiance
    // (the horizon glow and the halo around the disc both clear the threshold) into the bloom
    // pyramid and paints the exact global haze DESIGN.md 4 rules out by name. The stand-in below
    // reads 1.0 so the failure direction, if depth ever goes missing, is no extra glow at all.
    this.prefilterMat.uniforms.tDepth!.value = depth ?? this.skyTexture;
    this.prefilterMat.uniforms.tSrc!.value = source;
    (this.prefilterMat.uniforms.uTexel!.value as THREE.Vector2).set(1 / srcW, 1 / srcH);
    this.quad.material = this.prefilterMat;
    renderer.setRenderTarget(this.targets[0]!);
    renderer.clear(true, false, false);
    this.quad.render(renderer);

    // Down the pyramid.
    this.quad.material = this.downMat;
    for (let i = 1; i < this.targets.length; i++) {
      const src = this.targets[i - 1]!;
      this.downMat.uniforms.tSrc!.value = src.texture;
      (this.downMat.uniforms.uTexel!.value as THREE.Vector2).set(1 / src.width, 1 / src.height);
      renderer.setRenderTarget(this.targets[i]!);
      renderer.clear(true, false, false);
      this.quad.render(renderer);
    }

    // Back up, adding each level into the one above it.
    this.quad.material = this.upMat;
    for (let i = this.targets.length - 1; i >= 1; i--) {
      const src = this.targets[i]!;
      this.upMat.uniforms.tSrc!.value = src.texture;
      (this.upMat.uniforms.uTexel!.value as THREE.Vector2).set(1 / src.width, 1 / src.height);
      renderer.setRenderTarget(this.targets[i - 1]!);
      this.quad.render(renderer);
    }

    renderer.setRenderTarget(prevTarget);
    renderer.setClearColor(_clearColor, prevAlpha);
    renderer.autoClear = prevAutoClear;

    return this.targets[0]!.texture;
  }

  dispose(): void {
    for (const t of this.targets) t.dispose();
    this.targets = [];
    this.prefilterMat.dispose();
    this.downMat.dispose();
    this.upMat.dispose();
    this.blackTexture.dispose();
    this.skyTexture.dispose();
    this.quad.dispose();
  }
}

class SelectiveBloomPass extends Pass {
  constructor(
    private readonly chain: BloomChain,
    private readonly onResult: (t: THREE.Texture) => void,
  ) {
    super();
    this.needsSwap = false;
  }

  override render(
    renderer: THREE.WebGLRenderer,
    _writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    this.onResult(
      this.chain.render(
        renderer,
        readBuffer.texture,
        readBuffer.depthTexture ?? null,
        readBuffer.width,
        readBuffer.height,
      ),
    );
  }

  override setSize(width: number, height: number): void {
    this.chain.setSize(width, height);
  }

  override dispose(): void {
    this.chain.dispose();
  }
}

// ---------------------------------------------------------------------------
// The sunlit composite
// ---------------------------------------------------------------------------

const COMPOSITE_FRAG = /* glsl */ `
uniform sampler2D tDiffuse;
uniform sampler2D tBloom;
uniform sampler2D tAo;
uniform sampler2D tDepth;

uniform vec2 uSunScreen;
uniform float uShaft;
uniform vec3 uShaftColor;
uniform float uFlare;
uniform float uNoonGlare;
uniform float uNoonGamma;

uniform vec2 uResolution;
uniform float uAspect;
uniform float uTime;
uniform float uSpeed;
uniform float uBloomStrength;
uniform float uChromatic;
uniform float uRadialBlur;
uniform float uWind;
uniform float uSplash;
uniform vec3 uSplashColor;
uniform float uSplashSeed;
uniform float uVignette;
uniform float uExposure;
uniform float uSaturation;
uniform float uKnee;
uniform float uLift;
uniform vec3 uLiftColor;
uniform vec3 uWarmTint;
uniform vec3 uCoolTint;
uniform vec3 uAoTint;

varying vec2 vUv;

${HASH_GLSL}

const vec3 LUMA = vec3( 0.2126, 0.7152, 0.0722 );

/**
 * HIGH-KEY SHOULDER. Linear up to uKnee, then an exponential roll-off that approaches 1.0
 * without ever reaching it.
 *
 * ACES was the wrong operator for this game. Its shoulder starts almost immediately, so in
 * a world whose *subject matter* is white architecture it compressed the entire value
 * ladder — sunlit wall, shaded wall, floor, ceiling, cast shadow — into the top of the
 * range and handed back a white haze with no detail in it. Here everything that carries
 * structure sits below the knee and is passed through untouched; only genuine emitters
 * reach the shoulder, which is exactly where a soft roll-off is wanted.
 */
vec3 highKeyTonemap( vec3 x ) {

	x = max( x, vec3( 0.0 ) );
	float headroom = 1.0 - uKnee;
	vec3 over = max( x - uKnee, vec3( 0.0 ) );
	return min( x, vec3( uKnee ) ) + headroom * ( 1.0 - exp( - over / headroom ) );

}

vec3 linearToSRGB( vec3 c ) {

	c = max( c, vec3( 0.0 ) );
	vec3 lo = c * 12.92;
	vec3 hi = 1.055 * pow( c, vec3( 1.0 / 2.4 ) ) - 0.055;
	return mix( lo, hi, step( vec3( 0.0031308 ), c ) );

}

/**
 * Soft wind streaks. NOT comic speed lines (DESIGN.md 4 rules those out) — the angular
 * noise is smoothly interpolated, so these are soft-edged wisps in the far periphery, and
 * they only ever brighten, never replace.
 */
float windStreaks( vec2 c, float speed, float t ) {

	float rad = length( c ) * 2.0;
	float ang = atan( c.y, c.x ) * 0.15915494 + 0.5;
	float a = ang * 150.0;
	float f = smoothstep( 0.0, 1.0, fract( a ) );
	float n = mix( hash11( floor( a ) + floor( t * 12.0 ) * 3.7 ), hash11( floor( a ) + 1.0 + floor( t * 12.0 ) * 3.7 ), f );
	float soft = smoothstep( 0.66, 1.0, n );
	float band = smoothstep( 0.62, 1.05, rad ) * ( 1.0 - smoothstep( 1.3, 1.75, rad ) );
	return soft * band * speed;

}

/** Clean-edged saturated paint splash, plus a few thrown droplets. */
float splashMask( vec2 p, float seed ) {

	float r = length( p );
	float a = atan( p.y, p.x );

	float edge = 0.30
		+ 0.075 * sin( a * 7.0 + seed * 11.0 )
		+ 0.050 * sin( a * 13.0 - seed * 5.0 )
		+ 0.035 * sin( a * 3.0 + seed * 2.3 )
		+ 0.022 * sin( a * 23.0 + seed * 17.0 );

	float m = smoothstep( edge - 0.010, edge + 0.010, r );

	for ( int i = 0; i < 4; i ++ ) {
		float fi = float( i );
		float da = hash11( seed * 3.7 + fi ) * 6.2831853;
		float dr = 0.09 + 0.17 * hash11( seed * 9.1 + fi * 2.3 );
		vec2 dp = vec2( cos( da ), sin( da ) ) * dr;
		float ds = 0.012 + 0.030 * hash11( seed * 4.4 + fi * 5.1 );
		m = max( m, 1.0 - smoothstep( ds - 0.006, ds, length( p - dp ) ) );
	}

	return clamp( m, 0.0, 1.0 );

}

void main() {

	vec2 uv = vUv;
	vec2 c = uv - 0.5;
	float r2 = dot( c, c );
	float rad = length( c ) * 2.0;

	// IS THIS PIXEL THE SKY? Sampled at the UNSHIFTED uv on purpose: the chromatic offset and
	// the radial blur move where colour is read from, but they must not move where the frame
	// thinks the horizon is, or the grade would tear along every silhouette at speed. See
	// SKY_DEPTH for what this is for.
	float otSky = step( ${SKY_DEPTH.toFixed(6)}, texture2D( tDepth, vUv ).x );

	// --- chromatic aberration. Purely radial and cubic in radius, so it is identically zero
	//     at the centre of the frame and only ever touches the far periphery; and uChromatic
	//     is zero below CHROMATIC_SPEED_GATE, so a stationary player sees none at all. Visible
	//     RGB fringing on static geometry reads as a bug, and it was one.
	vec2 caDir = c * ( r2 * ${CHROMATIC_FALLOFF.toFixed(2)} );
	vec3 col;
	col.r = texture2D( tDiffuse, uv - caDir * uChromatic ).r;
	col.g = texture2D( tDiffuse, uv ).g;
	col.b = texture2D( tDiffuse, uv + caDir * uChromatic ).b;

	// --- radial speed blur. The centre stays sharp — you have to be able to aim at speed.
	//
	// The taps are JITTERED per pixel, and that is not a refinement — without it this effect is
	// the single worst artifact in the build. A handful of evenly spaced taps does not read as
	// blur across a high-contrast edge; it lays down that many discrete DISPLACED COPIES of the
	// edge. Against sky, every long diagonal roofline grew a comb of hard white stripes running
	// its whole length, which reads as a broken renderer rather than as motion.
	//
	// Offsetting each pixel's tap ladder by a hash turns those copies into per-pixel noise, which
	// the eye integrates as blur. Same tap count, same cost, no banding.
	if ( uRadialBlur > 0.0005 ) {
		float amt = uRadialBlur * smoothstep( 0.45, 1.25, rad );
		vec2 toCentre = - c;
		float jitter = hash12( gl_FragCoord.xy );
		float wsum = 1.0;
		for ( int i = 1; i <= ${RADIAL_TAPS}; i ++ ) {
			float tt = ( float( i ) - jitter ) / float( ${RADIAL_TAPS} );
			float w = 1.0 - tt * 0.6;
			col += texture2D( tDiffuse, uv + toCentre * ( amt * tt ) ).rgb * w;
			wsum += w;
		}
		col /= wsum;
	}

	// --- ambient occlusion: cool-tinted, subtle, contact only.
	float ao = texture2D( tAo, uv ).r;
	col *= mix( uAoTint, vec3( 1.0 ), clamp( ao, 0.0, 1.0 ) );

	// --- selective bloom
	col += texture2D( tBloom, uv ).rgb * uBloomStrength;

	// --- LIGHT SHAFTS. March toward the sun's screen position and count how many taps land on
	//     the far plane; those are the taps that can see sky. The result is a warm streak that
	//     only exists where a real opening lets daylight through, which is the whole point —
	//     it is the openings that have to read, not the frame.
	if ( uShaft > 0.001 ) {

		vec2 shaftStep = ( uSunScreen - vUv ) * ( ${SHAFT_DENSITY.toFixed(3)} / float( ${SHAFT_TAPS} ) );
		// Jitter the first sample by a per-pixel hash: twelve taps would otherwise quantise the
		// streak into twelve hard bars, and the bars would crawl as the camera turns.
		vec2 sp = vUv + shaftStep * hash12( gl_FragCoord.xy );
		float w = 1.0;
		float acc = 0.0;
		for ( int i = 0; i < ${SHAFT_TAPS}; i ++ ) {
			sp += shaftStep;
			float sd = texture2D( tDepth, clamp( sp, vec2( 0.0 ), vec2( 1.0 ) ) ).x;
			acc += step( ${SKY_DEPTH.toFixed(6)}, sd ) * w;
			w *= ${SHAFT_DECAY.toFixed(3)};
		}
		acc /= float( ${SHAFT_TAPS} );

		float sunFall = exp( - length( ( vUv - uSunScreen ) * vec2( uAspect, 1.0 ) ) * ${SHAFT_FALLOFF.toFixed(2)} );
		// Squared, so a ray that is only half unobstructed contributes a quarter. That is what
		// keeps the effect a shaft with an edge instead of a soft glow over everything.
		col += uShaftColor * ( acc * acc ) * uShaft * sunFall;

	}

	// --- LENS FLARE, and only when the sun is genuinely visible. See FLARE_STRENGTH.
	if ( uFlare > 0.0001 ) {

		// The occlusion test. Five depth taps in a small cross at the sun's screen position; a tap
		// on the far plane is a tap that can see sky. Averaging them is what makes the flare fade
		// across a few frames as a mast crosses the disc rather than switching on and off.
		vec2 sunUv = clamp( uSunScreen, vec2( 0.0 ), vec2( 1.0 ) );
		vec2 od = vec2( 0.011 / uAspect, 0.011 );
		float open = step( ${SKY_DEPTH.toFixed(6)}, texture2D( tDepth, sunUv ).x );
		open += step( ${SKY_DEPTH.toFixed(6)}, texture2D( tDepth, clamp( sunUv + vec2( od.x, 0.0 ), vec2( 0.0 ), vec2( 1.0 ) ) ).x );
		open += step( ${SKY_DEPTH.toFixed(6)}, texture2D( tDepth, clamp( sunUv - vec2( od.x, 0.0 ), vec2( 0.0 ), vec2( 1.0 ) ) ).x );
		open += step( ${SKY_DEPTH.toFixed(6)}, texture2D( tDepth, clamp( sunUv + vec2( 0.0, od.y ), vec2( 0.0 ), vec2( 1.0 ) ) ).x );
		open += step( ${SKY_DEPTH.toFixed(6)}, texture2D( tDepth, clamp( sunUv - vec2( 0.0, od.y ), vec2( 0.0 ), vec2( 1.0 ) ) ).x );
		open *= 0.2;

		// ...and fade it out as the disc leaves the frame, so nothing snaps at the screen edge.
		vec2 edge = smoothstep( vec2( - 0.05 ), vec2( 0.10 ), uSunScreen )
			* ( 1.0 - smoothstep( vec2( 0.90 ), vec2( 1.05 ), uSunScreen ) );
		float flareK = uFlare * open * edge.x * edge.y;

		if ( flareK > 0.0001 ) {

			// Aspect-corrected, so every shape here is round on screen rather than round in uv.
			vec2 d = ( vUv - uSunScreen ) * vec2( uAspect, 1.0 );
			float fd = length( d );

			float flare = exp( - fd * 7.0 ) * 0.55 + exp( - fd * 2.0 ) * 0.14;
			// Anamorphic streak: tight in y, wide in x, and soft at both ends.
			flare += exp( - abs( d.y ) * 150.0 ) * exp( - abs( d.x ) * 2.6 ) * 0.30;

			// Ghosts, on the sun -> centre axis. A ghost at multiple k sits at k * axis from the
			// sun, so one expression covers all three.
			vec2 axis = ( vec2( 0.5 ) - uSunScreen ) * vec2( uAspect, 1.0 );
${FLARE_GHOSTS.map(
  (g) => `			{
				vec2 gd = d - axis * ${g.at.toFixed(3)};
				flare += exp( - dot( gd, gd ) * ${g.tight.toFixed(1)} ) * ${g.gain.toFixed(3)};
			}`,
).join('\n')}

			col += uShaftColor * ( flare * flareK );

		}

	}

	// --- HIGH NOON sun glare. A soft warm disc centred on the sun's own screen position, so it
	//     has a source in frame even over a cloudless sky. Pre-tonemap, so the shoulder rolls it
	//     off exactly the way it rolls off the sun itself instead of letting it clip flat.
	if ( uNoonGlare > 0.0001 ) {
		float gd = length( ( vUv - uSunScreen ) * vec2( uAspect, 1.0 ) );
		col += uShaftColor * uNoonGlare * exp( - gd * ${NOON_GLARE_FALLOFF.toFixed(2)} );
	}

	// --- wind
	if ( uSpeed > 0.002 ) {
		col += vec3( 1.0, 1.02, 1.06 ) * windStreaks( c, uSpeed, uTime ) * uWind;
	}

	// --- CHROMA, in scene radiance and before the tonemap. Luminance-preserving by construction:
	//     mix( vec3( luma ), col, k ) has luminance exactly luma for any k, so the value ladder
	//     cannot move and a neutral white is untouched. All it does is stop the rest of this
	//     pipeline — shoulder, black lift, AO tint — from quietly greying the colour that WAS
	//     authored: the sky, the team blocks, the amber gun, the warm/cool split on concrete.
	//     See GRADE_SATURATION.
	col = mix( vec3( dot( col, LUMA ) ), col, uSaturation );

	// --- tonemap into display range
	col = highKeyTonemap( col * uExposure );

	// --- HIGH NOON contrast. A gamma, deliberately: it pins both ends of the range (white stays
	//     white, and the black point below is still what decides the floor) and darkens
	//     everything between on a smooth curve, so the frame gets harder without a single hue
	//     moving and without anything crushing. Uniform branch, so it is free when not in
	//     sudden death.
	if ( uNoonGamma > 1.0001 ) {
		col = pow( max( col, vec3( 0.0 ) ), vec3( uNoonGamma ) );
	}

	// --- grade: lift the black point so nothing ever crushes, then push highlights warm and
	//     shadows cool. This is the sunlit-cel look in three lines.
	//
	//     THE SKY DOES NOT TAKE THE TINT. Warm-highlight/cool-shadow is a claim about which
	//     light source lit a SURFACE, inferred from luminance; the sky is not a surface and the
	//     inference inverts on it, bleaching a blue dome toward warm white. sky.ts authors the
	//     dome's hue; the composite carries it to the screen unchanged. See GRADE_TINT_LO.
	col = col * ( 1.0 - uLift ) + uLiftColor * uLift;
	float l = dot( col, LUMA );
	vec3 tint = mix( uCoolTint, uWarmTint, smoothstep( ${GRADE_TINT_LO.toFixed(3)}, ${GRADE_TINT_HI.toFixed(3)}, l ) );
	col *= mix( tint, vec3( 1.0 ), otSky );

	// --- gentle vignette
	col *= 1.0 - uVignette * smoothstep( 0.10, 0.62, r2 );

	// --- impact frame
	if ( uSplash > 0.002 ) {
		vec2 sp = vec2( c.x * uAspect, c.y );
		float m = splashMask( sp, uSplashSeed );
		col = mix( col, uSplashColor, clamp( m * uSplash, 0.0, 1.0 ) );
	}

	vec3 srgb = linearToSRGB( col );

	// --- quantisation dither. NOT film grain (DESIGN.md 4 rules that out) — this is a
	//     sub-LSB triangular-PDF dither applied at the 8-bit encode. A high-key world is
	//     full of shallow near-white gradients, and without this they band into visible
	//     steps across the floor. Amplitude is one code value, so it is invisible.
	float d1 = hash12( gl_FragCoord.xy + fract( uTime ) * 137.0 );
	float d2 = hash12( gl_FragCoord.xy + 19.7 + fract( uTime ) * 311.0 );
	srgb += ( d1 + d2 - 1.0 ) * ( 1.0 / 255.0 );

	gl_FragColor = vec4( srgb, 1.0 );

}
`;

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

export interface PostFX {
  readonly composer: EffectComposer;
  setSize(cssWidth: number, cssHeight: number, pixelRatio: number): void;
  setQuality(q: RenderQuality): void;
  setSpeed01(v: number): void;
  /**
   * Where the sun is on screen, in uv, and how much of the light-shaft effect to run (0 when
   * the sun is behind the camera, where the projection is meaningless anyway). The renderer
   * owns this because only it knows both the sun direction and the camera.
   */
  setSunScreen(u: number, v: number, visibility: number): void;
  /**
   * HIGH NOON, 0..1. Pushes contrast and sun glare; changes no hue anywhere in the frame.
   * See NOON_GAMMA for why this is not the amber wash it replaces.
   */
  setHighNoon(t: number): void;
  splash(colorHex: number): void;
  render(dtSec: number, elapsedSec: number): void;
  dispose(): void;
}

export function createPostFX(
  gl: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
): PostFX {
  // Our own render target so we can hang a DepthTexture off it for the AO pass. The
  // composer clones it for its second ping-pong buffer, clone included.
  const size = gl.getDrawingBufferSize(new THREE.Vector2());
  const sceneTarget = new THREE.WebGLRenderTarget(Math.max(1, size.x), Math.max(1, size.y), {
    type: THREE.HalfFloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    depthBuffer: true,
    stencilBuffer: false,
    generateMipmaps: false,
    depthTexture: new THREE.DepthTexture(Math.max(1, size.x), Math.max(1, size.y)),
  });
  sceneTarget.texture.name = 'OneTapScene';

  const composer = new EffectComposer(gl, sceneTarget);
  composer.renderToScreen = true;

  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  const compositePass = new ShaderPass({
    name: 'OneTapSunlitComposite',
    uniforms: {
      tDiffuse: { value: null },
      tBloom: { value: null },
      tAo: { value: null },
      tDepth: { value: null },
      uSunScreen: { value: new THREE.Vector2(0.5, 1.2) },
      uShaft: { value: 0 },
      uShaftColor: { value: hueOnly(PALETTE.sun) },
      uFlare: { value: 0 },
      uNoonGlare: { value: 0 },
      uNoonGamma: { value: 1 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uAspect: { value: 1 },
      uTime: { value: 0 },
      uSpeed: { value: 0 },
      uBloomStrength: { value: BLOOM_STRENGTH },
      uChromatic: { value: 0 },
      uRadialBlur: { value: 0 },
      uWind: { value: WIND_STRENGTH },
      uSplash: { value: 0 },
      uSplashColor: { value: new THREE.Color(1, 1, 1) },
      uSplashSeed: { value: 0 },
      uVignette: { value: VIGNETTE },
      uExposure: { value: GRADE_EXPOSURE },
      uSaturation: { value: GRADE_SATURATION },
      uKnee: { value: GRADE_TONE_KNEE },
      uLift: { value: GRADE_SHADOW_LIFT },
      uLiftColor: { value: new THREE.Color().setHex(PALETTE.shadow, THREE.SRGBColorSpace) },
      uWarmTint: { value: WARM_TINT.clone() },
      uCoolTint: { value: COOL_TINT.clone() },
      uAoTint: { value: hueOnly(PALETTE.shadow).multiplyScalar(AO_FLOOR) },
    },
    vertexShader: FULLSCREEN_VERT,
    fragmentShader: COMPOSITE_FRAG,
  });

  // A 1x1 stand-in so the shaft march never samples an unbound sampler. It reads as "nothing
  // here is sky", i.e. no shafts, which is the correct answer when there is no depth buffer.
  const noDepth = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
  noDepth.name = 'ShaftDepthOff';
  noDepth.needsUpdate = true;
  compositePass.uniforms.tDepth!.value = noDepth;

  const depthTap = new DepthTapPass((t) => {
    compositePass.uniforms.tDepth!.value = t ?? noDepth;
  });
  composer.addPass(depthTap);

  const aoPass = new AmbientOcclusionPass(camera, (t) => {
    compositePass.uniforms.tAo!.value = t;
  });
  composer.addPass(aoPass);

  const chain = new BloomChain();
  const bloomPass = new SelectiveBloomPass(chain, (t) => {
    compositePass.uniforms.tBloom!.value = t;
  });
  composer.addPass(bloomPass);
  composer.addPass(compositePass);

  const fxaaPass = new ShaderPass(FXAAShader);
  composer.addPass(fxaaPass);

  let radialBlurEnabled = true;
  let sunVisibility = 0;
  let noon = 0;
  let targetSpeed = 0;
  let shownSpeed = 0;
  let splashValue = 0;
  let splashHoldFrames = 0;
  let splashSeed = 0;

  const u = compositePass.uniforms;

  function setSize(cssWidth: number, cssHeight: number, pixelRatio: number): void {
    const w = Math.max(1, Math.round(cssWidth));
    const h = Math.max(1, Math.round(cssHeight));
    const deviceW = Math.max(1, Math.round(w * pixelRatio));
    const deviceH = Math.max(1, Math.round(h * pixelRatio));

    composer.setPixelRatio(pixelRatio);
    composer.setSize(w, h);

    (u.uResolution!.value as THREE.Vector2).set(deviceW, deviceH);
    u.uAspect!.value = deviceW / deviceH;
    (fxaaPass.uniforms.resolution!.value as THREE.Vector2).set(1 / deviceW, 1 / deviceH);
  }

  /**
   * Everything the sun does — shafts, glare, bloom gain — is derived here rather than written
   * at each call site, so `setSunScreen` and `setHighNoon` can arrive in either order and in
   * any frame without the two disagreeing about how bright the sun currently is.
   */
  function applySunUniforms(): void {
    u.uShaft!.value = SHAFT_STRENGTH * sunVisibility * (1 + NOON_SHAFT * noon);
    // The flare's OTHER gate. `sunVisibility` only knows the sun is in front of the camera; the
    // shader's depth taps are what decide whether it is actually unoccluded. Both have to agree.
    u.uFlare!.value = FLARE_STRENGTH * sunVisibility * (1 + FLARE_NOON * noon);
    u.uNoonGlare!.value = NOON_GLARE * noon * sunVisibility;
    u.uNoonGamma!.value = 1 + NOON_GAMMA * noon;
    u.uBloomStrength!.value = BLOOM_STRENGTH * (1 + NOON_BLOOM * noon);
  }

  function setQuality(q: RenderQuality): void {
    chain.setLevels(BLOOM_LEVELS[q]);
    // AO and the radial blur are the passes worth spending, and both go on integrated; only
    // the very bottom tier gives them up. The SUN SHAFTS do not: twelve taps of a depth texture
    // in a pass that is already running is not what makes a machine slow, and switching off the
    // effect that says "this game is outdoors" on the tier an automated capture is most likely
    // to land on has already cost this project two review rounds via the shadow map.
    aoPass.setEnabled(q !== 'low');
    radialBlurEnabled = q !== 'low';
    applySunUniforms();
  }

  setQuality('high');

  return {
    composer,

    setSize,
    setQuality,

    setSpeed01(v: number): void {
      targetSpeed = v < 0 ? 0 : v > 1 ? 1 : v;
    },

    setSunScreen(su: number, sv: number, visibility: number): void {
      (u.uSunScreen!.value as THREE.Vector2).set(su, sv);
      sunVisibility = visibility < 0 ? 0 : visibility > 1 ? 1 : visibility;
      applySunUniforms();
    },

    setHighNoon(t: number): void {
      noon = t < 0 ? 0 : t > 1 ? 1 : t;
      applySunUniforms();
    },

    splash(colorHex: number): void {
      (u.uSplashColor!.value as THREE.Color).setHex(colorHex, THREE.SRGBColorSpace);
      // Golden-ratio stepping so consecutive kills never repeat the same splat shape.
      splashSeed = (splashSeed + 0.6180339887) % 1;
      u.uSplashSeed!.value = splashSeed;
      splashValue = 1;
      splashHoldFrames = 2;
    },

    render(dtSec: number, elapsedSec: number): void {
      shownSpeed = targetSpeed + (shownSpeed - targetSpeed) * Math.exp(-SPEED_DAMP * dtSec);
      u.uSpeed!.value = shownSpeed;
      u.uTime!.value = elapsedSec;
      // Hard-gated on speed: below the gate the offset is exactly zero, so the three colour
      // taps land on the same texel and there is provably no fringing at a standstill.
      const caGate =
        shownSpeed <= CHROMATIC_SPEED_GATE
          ? 0
          : (shownSpeed - CHROMATIC_SPEED_GATE) / (1 - CHROMATIC_SPEED_GATE);
      u.uChromatic!.value = CHROMATIC_MAX * caGate * caGate;
      u.uRadialBlur!.value = radialBlurEnabled ? RADIAL_BLUR_MAX * shownSpeed * shownSpeed : 0;

      if (splashHoldFrames > 0) {
        splashHoldFrames--;
        splashValue = 1;
      } else if (splashValue > 0) {
        splashValue *= Math.exp(-SPLASH_DECAY * dtSec);
        if (splashValue < 0.004) splashValue = 0;
      }
      u.uSplash!.value = splashValue;

      // Deterministic ping-pong: RenderPass always writes the buffer whose depth texture the
      // AO pass then reads. (The composer would end up here anyway, but not by contract.)
      composer.writeBuffer = composer.renderTarget1;
      composer.readBuffer = composer.renderTarget2;

      composer.render(dtSec);
    },

    dispose(): void {
      composer.dispose();
      chain.dispose();
      aoPass.dispose();
      compositePass.dispose();
      fxaaPass.dispose();
      renderPass.dispose();
      depthTap.dispose();
      noDepth.dispose();
    },
  };
}
